/**
 * PrintRouteContextService
 *
 * Builds the {@link PrintRouteContext} that {@link resolvePrintRoute} takes —
 * once, from the three sources that are not the facility — so every screen that
 * prints asks the same question of the same facts.
 *
 * Why a service and not four literals:
 *   The context has four fields, and each one has exactly one honest source: the
 *   device (ScreenSizeService), the standing config (`printMode.mobile`), the
 *   facility's printers (locations-config.json), and the device-local outage
 *   switch (PrintFallbackService). Assembling it at the call site invites a
 *   screen to answer one of them from what it knows about itself instead — "this
 *   modal is opened from the log table, so isMobile is false", "the desktop form
 *   always browser-prints". Both of those were written down, both were wrong, and
 *   the second one meant the Printing screen told the operator their labels were
 *   coming off the Zebra while the form quietly sent them to a Dymo. Which screen
 *   you are on is not an input to the routing decision; the surface and the
 *   facility are.
 *
 * Why the facility is a parameter and not a fourth source read in here:
 *   It is the one field the caller legitimately knows better. The new-log
 *   surfaces route for the facility the operator has selected; the log table's
 *   reprint routes for the facility stored on the row, which is not necessarily
 *   the same one. Every caller has already resolved that Location for its own
 *   reasons — its printer names, its QR setting — so taking it as an argument
 *   costs nothing and keeps this service out of the location-lookup business,
 *   which belongs to AssetsDataService and SelectedLocationService.
 *
 * Snapshot, not a stream:
 *   Returns a plain object read at call time. The fallback mode and the selected
 *   facility can both change while a screen is open, and the screens that care
 *   already subscribe to those sources and re-ask — see ReviewStepComponent's
 *   `_syncLocationPrintConfig`. A screen that must NOT follow a mid-flight change
 *   (LogFinishedComponent, which fires its print on arrival) keeps the object it
 *   was handed. Pushing an observable here would take that choice away from both.
 */

import { Injectable, inject } from '@angular/core';
import { AssetsDataService } from './assets-data.service';
import { PrintFallbackService } from './print-fallback.service';
import { ScreenSizeService } from './screen-size.service';
import { Location } from '../models/assets-data/LocationConfig.interface';
import { PrintMode } from '../models/assets-data/receiving/ReceivingLogFormConfig.interface';
import {
  PrintRouteContext,
  locationCapability,
} from '../domain/receiving-log/rules/print-path.rules';

@Injectable({
  providedIn: 'root',
})
export class PrintRouteContextService {
  // #region Dependencies
  private readonly _assetsData = inject(AssetsDataService);
  private readonly _printFallback = inject(PrintFallbackService);
  private readonly _screenSize = inject(ScreenSizeService);
  // #endregion

  // #region Building the context
  /**
   * Everything {@link resolvePrintRoute} needs, for one facility.
   *
   * An unknown facility (undefined — the config has not loaded, or the label
   * matches nothing) reads as "no printers", which routes to the browser. That
   * failure direction is deliberate: browser-printing is always safe, whereas
   * guessing a backend printer queues a job at hardware nobody can find.
   *
   * @param location - The facility's config entry, or undefined when unknown.
   */
  public forLocation(location: Location | null | undefined): PrintRouteContext {
    return {
      isMobile: this._screenSize.isMobile.getValue(),
      configuredMode: this._configuredMode(),
      capability: locationCapability(location),
      fallbackMode: this._printFallback.mode,
    };
  }
  // #endregion

  // #region Internals
  /**
   * `printMode.mobile` from the receiving-log form config — the standing escape
   * hatch that forces the browser path even where a backend printer exists.
   *
   * Guarded, because the config read throws before the assets data has loaded and
   * a screen that opens in that window must still be able to route. LogFinished
   * carried this guard alone; pulling it in here gives it to every caller.
   *
   * Failing to `auto` rather than to `browser` keeps the answer with the
   * facility: a location with no printer routes to the browser on its own, and a
   * location with one should not be diverted off it by a load-order accident.
   */
  private _configuredMode(): PrintMode {
    try {
      return this._assetsData.GetPrintModeDefaults().mobile;
    } catch {
      return 'auto';
    }
  }
  // #endregion
}
